import {
  Button,
  DatePicker,
  Drawer,
  Form,
  InputNumber,
  Select,
  Space,
} from "antd";
import Input from "antd/es/input/Input";
import { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  useMap,
  Polygon,
  Tooltip,
} from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import L from "leaflet";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { GeoJSON } from "react-leaflet";
import * as turf from "@turf/turf";
import toast from "react-hot-toast";
import {
  AdresseBody,
  TrancheDetailsModel,
} from "../../../domaine/projectModel";
import { addAdresseApi } from "../../../api/projects.api";
import { useDispatch } from "react-redux";
import {
  addTranche,
  fetchProjectDetails,
} from "../../../store/slices/projectSlice";

// Fix Leaflet icon issues
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Component to force map invalidation when drawer opens
function MapInvalidator() {
  const map = useMap();
  const isMounted = useRef(true);

  useEffect(() => {
    // Setup invalidation with error handling
    const timer = setTimeout(() => {
      if (
        isMounted.current &&
        map &&
        document.body.contains(map.getContainer())
      ) {
        try {
          map.invalidateSize(true);
        } catch (err) {
          console.warn("Failed to invalidate map size:", err);
        }
      }
    }, 300); // Slightly longer timeout

    // Cleanup function
    return () => {
      isMounted.current = false;
      clearTimeout(timer);
    };
  }, [map]);

  return null;
}

interface Props {
  setAddDrawerVisible: any;
  addDrawerVisible: boolean;
  projectId: number;
  communeObjectId: number | null;
}

interface AddressData {
  longitude?: number;
  latitude?: number;
  address?: string;
  coordinates?: Array<[number, number]>;
  fk_commune?: number | null;
}

export const AddTrancheDrawer: React.FC<Props> = ({
  setAddDrawerVisible,
  addDrawerVisible,
  projectId,
  communeObjectId,
}) => {
  const [isTrancheEnChantier, setIsTrancheEnChantier] = useState(false);
  const [requireSurfaceFields, setRequireSurfaceFields] = useState(false);
  const [form] = Form.useForm();
  const dateFormat = "DD/MM/YYYY";
  const [drawnItems, setDrawnItems] = useState<L.FeatureGroup | null>(null);
  const [addressData, setAddressData] = useState<AddressData>({});
  const [mapReady, setMapReady] = useState(false);
  const { projectDetails } = useSelector((state: RootState) => state.projects);
  const [communesData, setCommunesData] = useState<any>(null);
  const [existingTranches, setExistingTranches] = useState<L.Polygon[]>([]);

  const [communeLayer, setCommuneLayer] = useState<L.GeoJSON | null>(null);

  const dispatch = useDispatch();

  const initialState = {
    name: null,
    surface_fonciere: null,
    surface_construite: null,
    surface_vendable: null,
    date_de_permis_de_construire: null,
    date_permis_d_habiter: null,
    phase_du_projet: null,
    etat_avancement: null,
    a_visiter: true,
    suivi_initial: true,
    adresse: null,
  };
  const [formData, setFormData] = useState(initialState);

  // Initial map center (France)
  const defaultCenter = [
    projectDetails.tranches[0].fk_adresse.coordonnees_latitude,
    projectDetails.tranches[0].fk_adresse.coordonnees_longitude,
  ];
  const defaultZoom = 16;

  // Initialize map after drawer is opened
  useEffect(() => {
    if (addDrawerVisible) {
      // First set map ready to false to ensure clean initialization
      setMapReady(false);

      // Then after a delay, set it to true
      const timer = setTimeout(() => {
        setMapReady(true);

        // Force re-render after map is ready
        if (drawnItems) {
          const tempLayer = drawnItems;
          setDrawnItems(null);
          setTimeout(() => setDrawnItems(tempLayer), 0);
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [addDrawerVisible]);

  // Check if surface fields should be required
  const checkSurfaceFieldsRequired = (
    phase: string | null,
    etat: string | null
  ) => {
    if (!phase) return false;

    // Convert to lowercase for case-insensitive comparison
    const phaseLower = phase.toLowerCase();

    // Required for "en livraison" or "livré"
    if (phaseLower === "en livraison" || phaseLower === "livré") {
      return true;
    }

    // Required for "en chantier" with specific etat_avancement values
    if (phaseLower === "en chantier" && etat) {
      const etatLower = etat.toLowerCase();
      return etatLower === "gros oeuvres" || etatLower === "finition";
    }

    return false;
  };

  useEffect(() => {
    if (mapReady && communeObjectId) {
      import("../../../data/communes_geo_data.min.json").then((data) => {
        setCommunesData(data);
      });
    }
  }, [mapReady, communeObjectId]);

  // Create a component to handle zooming to the commune
  function ZoomToCommune({
    communesData,
    communeObjectId,
  }: {
    communesData: any;
    communeObjectId: number;
  }) {
    const map = useMap();
    const hasZoomed = useRef(false);

    useEffect(() => {
      if (communesData && communeObjectId && !hasZoomed.current) {
        // Create a temporary GeoJSON layer to get bounds
        const tempLayer = L.geoJSON(communesData, {
          filter: (feature) =>
            feature.properties &&
            feature.properties.OBJECTID === communeObjectId,
        });

        if (tempLayer && map) {
          const bounds = tempLayer.getBounds();
          if (bounds.isValid()) {
            map.fitBounds(bounds, { padding: [50, 50] });
            hasZoomed.current = true;
          }
        }
      }
    }, [communesData, communeObjectId, map]);

    return null;
  }

  // Style function for the commune
  const getStyleForCommune = (feature: any) => {
    if (feature.properties && feature.properties.OBJECTID === communeObjectId) {
      return {
        weight: 3,
        opacity: 1,
        color: "#FF4500", // Orange-red border color
        fillOpacity: 0, // No fill
        dashArray: "5, 5", // Optional: dashed line for better visibility
      };
    }
    // Return null for other communes so they won't be rendered
    return {
      opacity: 0,
      fillOpacity: 0,
    };
  };

  const CommuneGeoJSON = ({
    data,
    style,
    filter,
    onLayerReady,
  }: {
    data: any;
    style: any;
    filter: any;
    onLayerReady: (layer: L.GeoJSON) => void;
  }) => {
    const layerRef = useRef<L.GeoJSON | null>(null);
    const map = useMap();
    const hasZoomed = useRef(false);

    // useEffect(() => {
    //   if (!data || hasZoomed.current) return;

    //   const tempLayer = L.geoJSON(data, { filter, style });
    //   const bounds = tempLayer.getBounds();

    //   if (bounds.isValid()) {
    //     // Delay the zoom slightly to ensure layer is mounted
    //     setTimeout(() => {
    //       map.fitBounds(bounds, { padding: [50, 50] });
    //       hasZoomed.current = true;
    //     }, 0);
    //   }

    //   if (layerRef.current) {
    //     onLayerReady(layerRef.current);
    //   }
    // }, [data, filter, style, map, onLayerReady]);

    return (
      <GeoJSON
        ref={layerRef}
        data={data}
        style={style}
        filter={filter}
        // onEachFeature={(feature, layer) => {
        //   if (feature.properties && feature.properties.NAME) {
        //     layer.bindTooltip(feature.properties.NAME, {
        //       permanent: true,
        //     });
        //   }
        // }}
      />
    );
  };

  // Filter function to only include the commune with matching OBJECTID
  const filterCommune = (feature: any) => {
    return (
      feature.properties && feature.properties.OBJECTID === communeObjectId
    );
  };
  const trancheToPolygon = (tranche: TrancheDetailsModel): L.Polygon | null => {
    if (!tranche.fk_adresse || !tranche.fk_adresse.coordonnees_polygone) {
      return null;
    }

    // Convert coordinates to LatLng array
    const latLngs = tranche.fk_adresse.coordonnees_polygone.map(
      (coord) => new L.LatLng(coord[0], coord[1])
    );

    return L.polygon([latLngs], {
      color: "#3388ff",
      weight: 2,
      opacity: 1,
      fillOpacity: 0.2,
    });
  };

  // In your useEffect, filter out null values
  useEffect(() => {
    if (addDrawerVisible && projectDetails && projectDetails.tranches) {
      const tranchePolygons = projectDetails.tranches
        .map(trancheToPolygon)
        .filter((polygon): polygon is L.Polygon => polygon !== null);
      setExistingTranches(tranchePolygons);
    } else {
      setExistingTranches([]);
    }
  }, [addDrawerVisible, projectDetails]);

  // The style function for existing tranches
  const existingTrancheStyle = {
    color: "#3388ff",
    weight: 2,
    opacity: 1,
    fillOpacity: 0.2,
  };

  const handleValuesChange = (changedValues: any, allValues: any) => {
    // Handle en chantier phase
    if (changedValues.phase_du_projet !== undefined) {
      const isEnChantier =
        changedValues.phase_du_projet?.toLowerCase() === "en chantier";
      setIsTrancheEnChantier(isEnChantier);

      if (!isEnChantier) {
        setFormData((prev: any) => ({ ...prev, etat_avancement: null }));
        form.setFieldsValue({ etat_avancement: null });
      }
    }

    // Check if surface fields should be required
    const shouldRequire = checkSurfaceFieldsRequired(
      allValues.phase_du_projet,
      allValues.etat_avancement
    );

    setRequireSurfaceFields(shouldRequire);

    // Revalidate the form when requirement changes
    if (
      changedValues.phase_du_projet !== undefined ||
      changedValues.etat_avancement !== undefined
    ) {
      form
        .validateFields(["surface_construite", "surface_vendable"])
        .catch(() => {
          // Ignore validation errors
        });
    }

    // Revalidate surface_vendable when surface_construite changes
    if (
      changedValues.surface_construite !== undefined ||
      changedValues.surface_vendable !== undefined
    ) {
      form.validateFields(["surface_vendable"]).catch(() => {
        // Ignore validation errors
      });
    }
  };

  const handleAddSubmit = () => {
    try {
      form
        .validateFields()
        .then(async (values: any) => {
          // First disable map to prevent interactions
          setMapReady(false);

          // Format dates for submission
          const formattedValues = {
            ...values,
            date_de_permis_de_construire: values.date_de_permis_de_construire
              ? values.date_de_permis_de_construire.format(dateFormat)
              : null,
            date_permis_d_habiter: values.date_permis_d_habiter
              ? values.date_permis_d_habiter.format(dateFormat)
              : null,
          };
          if (formattedValues.phase_du_projet === "livré") {
            formattedValues.a_visiter = false;
            formattedValues.suivi_initial = false;
          }
          console.log("formatted values", formattedValues);
          console.log(addressData);
          if (Object.keys(addressData).length === 0) {
            setMapReady(true);
            toast.error("Veuillez dessiner la tranche sur la carte.");
            return;
          }

          // Convert addressData to AdresseBody type
          const adresseBody: AdresseBody = {
            adresse: addressData.address || "", // Assuming address is the property name
            coordonnees_longitude: addressData.longitude || 0,
            coordonnees_latitude: addressData.latitude || 0,
            fk_commune: addressData.fk_commune || 0,
            coordonnees_polygone: addressData.coordinates || [],
          };
          const addressId = await addAdresseApi(adresseBody, "addresses/");

          const trancheBody = {
            ...formattedValues,
            fk_adresse: addressId,
            fk_projet: projectId,
          };
          await dispatch(addTranche(trancheBody));

          // Add a small delay before closing drawer
          setTimeout(() => {
            
            setAddDrawerVisible(false);
            setMapReady(false);
          }, 100);

          await dispatch(fetchProjectDetails(projectId));
          form.resetFields();
        })

        .catch((error) => {
          console.error("Error submitting form:", error);
        });
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setAddDrawerVisible(false);
  };

  // Custom validator for surface_vendable
  const validateSurfaceVendable = (_: any, value: number) => {
    const surfaceConstruite = form.getFieldValue("surface_construite");

    if (value && surfaceConstruite) {
      const minimumExpected = surfaceConstruite * 0.75;

      if (value < minimumExpected) {
        // Return warning instead of rejection
        return Promise.resolve({
          warning: true,
          warningMessage: `Surface vendable inférieure à 75% de la surface construite (minimum recommandé: ${minimumExpected.toFixed(
            2
          )} m²)`,
        });
      }
    }

    return Promise.resolve();
  };

  // Add this in your component
  useEffect(() => {
    // Cleanup when drawer closes
    if (!addDrawerVisible) {
      if (drawnItems) {
        drawnItems.clearLayers();
      }
      setMapReady(false);
    }
  }, [addDrawerVisible]);

  // And modify the drawer close handler
  const handleCloseDrawer = () => {
    // Set map ready to false first to ensure map components unmount properly
    setMapReady(false);

    // Add a small delay before completely closing drawer
    setTimeout(() => {
      setAddDrawerVisible(false);
    }, 100);
  };

  // Extract coordinates from a Leaflet polygon
  const extractPolygonCoordinates = (
    layer: L.Polygon
  ): Array<[number, number]> => {
    const coordinates: Array<[number, number]> = [];
    const latLngs = layer.getLatLngs();

    // Handle the nested array structure properly
    if (latLngs.length > 0) {
      // The first element contains the outer ring of the polygon
      const outerRing = latLngs[0];

      // Check if it's an array of LatLng objects
      if (Array.isArray(outerRing)) {
        outerRing.forEach((latLng: any) => {
          coordinates.push([latLng.lat, latLng.lng]);
        });
      }
    }

    return coordinates;
  };

  const isPolygonIntersectingCommune = (layer: L.Polygon): boolean => {
    if (!communeLayer) return true; // If no commune layer, allow all polygons
    try {
      // Get the GeoJSON from the commune layer
      const communeGeoJSON = communeLayer.toGeoJSON();

      // Handle both Feature and FeatureCollection cases
      const communeFeature =
        communeGeoJSON.type === "FeatureCollection"
          ? communeGeoJSON.features[0]
          : communeGeoJSON;

      // If we can't get a feature, allow the polygon (fail open)
      if (!communeFeature) {
        console.warn("No commune feature found, allowing polygon");
        return true;
      }

      // Get GeoJSON from the drawn polygon
      const polygonGeoJSON = layer.toGeoJSON();

      // Debug prints
      console.log("Commune feature:", communeFeature);
      console.log("Polygon feature:", polygonGeoJSON);

      // Check if polygon intersects OR is within the commune
      const intersects = turf.booleanIntersects(polygonGeoJSON, communeFeature);
      const within = turf.booleanWithin(polygonGeoJSON, communeFeature);

      console.log("Intersection result:", intersects || within);

      return intersects || within;
    } catch (error) {
      console.error("Error checking polygon intersection:", error);
      // If there's an error, allow the polygon (fail open)
      return true;
    }
  };

  // Handle polygon creation
  const handleCreated = async (e: any) => {
    const layer = e.layer;

    // Check if the polygon intersects with the commune
    if (layer instanceof L.Polygon && !isPolygonIntersectingCommune(layer)) {
      // Remove the layer if it doesn't intersect
      if (drawnItems) {
        drawnItems.removeLayer(layer);
      }
      // Show error message
      toast.error(
        "Le polygone doit avoir au moins un point à l'intérieur des limites de la commune."
      );
      return;
    }

    // Clear existing layers if there are any
    if (drawnItems) {
      drawnItems.clearLayers();
      drawnItems.addLayer(layer);
    }

    // Extract polygon coordinates if it's a polygon
    if (layer instanceof L.Polygon) {
      const coordinates = extractPolygonCoordinates(layer);

      // Calculate center of polygon
      const bounds = layer.getBounds();
      const center = bounds.getCenter();

      // Perform reverse geocoding to get address
      try {
        // Add user-agent header to comply with OpenStreetMap usage policy
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${center.lat}&lon=${center.lng}`,
          {
            headers: {
              "User-Agent": "YourAppName/1.0 (your@email.com)", // Replace with your app details
            },
          }
        );
        const data = await response.json();

        // Update form data
        const newAddressData = {
          latitude: center.lat,
          longitude: center.lng,
          address: data.display_name,
          coordinates: coordinates,
          fk_commune: communeObjectId,
        };

        setAddressData(newAddressData);

        // Update the form field
        form.setFieldsValue({
          adresse_display: data.display_name,
        });
      } catch (error) {
        console.error("Error with reverse geocoding:", error);
      }
    }
  };

  // Handle polygon editing
  const handleEdited = (e: any) => {
    const layers = e.layers;
    layers.eachLayer(async (layer: L.Layer) => {
      if (layer instanceof L.Polygon) {
        // Extract new coordinates
        const coordinates = extractPolygonCoordinates(layer);

        // Calculate new center
        const bounds = layer.getBounds();
        const center = bounds.getCenter();

        // Perform reverse geocoding with new center
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${center.lat}&lon=${center.lng}`,
            {
              headers: {
                "User-Agent": "YourAppName/1.0 (your@email.com)", // Replace with your app details
              },
            }
          );
          const data = await response.json();

          // Update form data
          const newAddressData = {
            latitude: center.lat,
            longitude: center.lng,
            address: data.display_name,
            coordinates: coordinates,
          };

          setAddressData(newAddressData);

          // Update the form field
          form.setFieldsValue({
            adresse_display: data.display_name,
          });
        } catch (error) {
          console.error("Error with reverse geocoding:", error);
        }
      }
    });
  };

  useEffect(() => {
    // When drawer is closing
    if (!addDrawerVisible) {
      if (drawnItems) {
        // Clear any drawn items
        drawnItems.clearLayers();
      }
      setAddressData({});
      setMapReady(false);
    }
  }, [addDrawerVisible, drawnItems]);

  function MapInitializer() {
    const map = useMap();
    const isMounted = useRef(true);

    useEffect(() => {
      const timer = setTimeout(() => {
        if (
          isMounted.current &&
          map &&
          document.body.contains(map.getContainer())
        ) {
          try {
            map.invalidateSize(true);
          } catch (err) {
            console.warn("Failed to invalidate map size:", err);
          }
        }
      }, 300);

      return () => {
        isMounted.current = false;
        clearTimeout(timer);
      };
    }, [map]);

    return null;
  }

  // Handle polygon deletion
  const handleDeleted = () => {
    setAddressData({});
    form.setFieldsValue({
      adresse_display: "",
    });
  };

  return (
    <Drawer
      title="Ajouter une tranche"
      width={700}
      onClose={handleCloseDrawer}
      open={addDrawerVisible}
      extra={
        <Space>
          <Button onClick={handleCancel}>Annuler</Button>
          <Button onClick={handleAddSubmit} type="primary">
            Ajouter
          </Button>
        </Space>
      }
    >
      <Form
        initialValues={formData}
        onValuesChange={handleValuesChange}
        form={form}
        layout="vertical"
        name="trancheEditForm"
      >
        <Form.Item
          name="name"
          label="Nom"
          rules={[
            {
              required: true,
              message: "Veuillez saisir le nom de la tranche",
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="date_de_permis_de_construire"
          label="Date permis de construire"
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const datePermisHabiter = getFieldValue(
                  "date_permis_d_habiter"
                );
                if (
                  value &&
                  datePermisHabiter &&
                  value.isAfter(datePermisHabiter)
                ) {
                  return Promise.reject(
                    new Error(
                      "La date de permis de construire doit être antérieure à la date de permis d'habiter."
                    )
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <DatePicker style={{ width: "100%" }} format={dateFormat} />
        </Form.Item>
        <Form.Item
          name="date_permis_d_habiter"
          label="Date permis d'habiter"
          rules={[
            // {
            //   required: true,
            //   message:
            //     "Veuillez saisir la date de permis d'habiter",
            // },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const datePermisConstruire = getFieldValue(
                  "date_de_permis_de_construire"
                );
                if (
                  value &&
                  datePermisConstruire &&
                  value.isBefore(datePermisConstruire)
                ) {
                  return Promise.reject(
                    new Error(
                      "La date de permis d'habiter doit être postérieure à la date de permis de construire."
                    )
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <DatePicker style={{ width: "100%" }} format={dateFormat} />
        </Form.Item>
        <Form.Item
          label="Phase de la tranche"
          name="phase_du_projet"
          rules={[
            {
              required: true,
              message: "Veuillez saisir la phase du projet",
            },
          ]}
        >
          <Select>
            <Select.Option value="en chantier">En chantier</Select.Option>
            <Select.Option value="en livraison">En livraison</Select.Option>
            <Select.Option value="livré">Livré</Select.Option>
          </Select>
        </Form.Item>

        {/* Only show etat_avancement if this specific tranche is en chantier */}
        {isTrancheEnChantier && (
          <Form.Item
            label="Etat d'avancement des travaux de construction"
            name="etat_avancement"
            rules={[{ required: isTrancheEnChantier }]}
          >
            <Select>
              <Select.Option value="Inconnu">Inconnu</Select.Option>
              <Select.Option value="Phase étude">Phase étude</Select.Option>
              <Select.Option value="Terrasement">Terrasement</Select.Option>
              <Select.Option value="Viabilisation">Viabilisation</Select.Option>
              <Select.Option value="Gros Oeuvres">Gros Oeuvres</Select.Option>
              <Select.Option value="Finition">Finition</Select.Option>
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="surface_fonciere"
          label="Surface foncière"
          rules={[
            {
              required: true,
              message: "Veuillez saisir la surface foncière",
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            type="number"
            addonAfter="m²"
          />
        </Form.Item>
        <Form.Item
          name="surface_construite"
          label="Surface construite"
          rules={[
            {
              required: requireSurfaceFields,
              message: "Veuillez saisir la surface construite",
            },
          ]}
        >
          <InputNumber
            onChange={(value: any) => {
              // Calculate 75% of surface_construite and set it to surface_vendable
              if (value) {
                const surfaceVendable = parseFloat((value * 0.75).toFixed(2));
                form.setFieldsValue({
                  surface_vendable: surfaceVendable,
                });

                // Trigger validation of surface_vendable
                form.validateFields(["surface_vendable"]);
              }
            }}
            style={{ width: "100%" }}
            type="number"
            addonAfter="m²"
          />
        </Form.Item>
        <Form.Item
          name="surface_vendable"
          label="Surface vendable"
          rules={[
            {
              required: requireSurfaceFields,
              message: "Veuillez saisir la surface vendable",
            },
            {
              validator: validateSurfaceVendable,
            },
          ]}
        >
          <InputNumber style={{ width: "100%" }} addonAfter="m²" />
        </Form.Item>

        {/* Map section - only render when drawer is open */}
        <Form.Item label="Adresse (dessinez un polygone sur la carte)" required>
          <div style={{ height: "400px", width: "100%", marginBottom: "10px" }}>
            {mapReady && (
              <MapContainer
                center={defaultCenter as [number, number]}
                zoom={defaultZoom}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  attribution="Google Maps"
                  url="https://www.google.cn/maps/vt?lyrs=s@189&gl=cn&x={x}&y={y}&z={z}"
                />
                <TileLayer
                  attribution="OpenStreetMap/Carto"
                  url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
                />
                {/* Render existing tranches as polygons */}
                {existingTranches.map((polygon, index) => {
                  // Handle the nested structure correctly
                  const latLngs = polygon.getLatLngs();

                  // Convert the nested structure to the format expected by the Polygon component
                  const positions = Array.isArray(latLngs[0])
                    ? // If it's already an array of arrays, process it
                      latLngs[0].map((latLng: any) => [latLng.lat, latLng.lng])
                    : // If it's a nested structure with deeper levels
                      [
                        latLngs.map((ring: any) =>
                          Array.isArray(ring)
                            ? ring.map((latLng: any) => [
                                latLng.lat,
                                latLng.lng,
                              ])
                            : [ring.lat, ring.lng]
                        ),
                      ];

                  return (
                    <Polygon
                      key={index}
                      positions={positions as any}
                      pathOptions={existingTrancheStyle}
                    >
                      {projectDetails?.tranches[index]?.name && (
                        <Tooltip permanent>
                          {projectDetails?.tranches[index]?.name ||
                            `Tranche ${index}`}
                        </Tooltip>
                      )}
                    </Polygon>
                  );
                })}
                {communesData && communeObjectId && (
                  <CommuneGeoJSON
                    data={communesData}
                    style={getStyleForCommune}
                    filter={filterCommune}
                    onLayerReady={(layer) => setCommuneLayer(layer)}
                  />
                )}
                <FeatureGroup ref={setDrawnItems}>
                  <EditControl
                    position="topright"
                    onCreated={handleCreated}
                    onEdited={handleEdited}
                    onDeleted={handleDeleted}
                    draw={{
                      rectangle: false,
                      circle: false,
                      marker: false,
                      polyline: false,
                      circlemarker: false,
                      polygon: drawnItems?.getLayers().length ? false : {},
                    }}
                  />
                </FeatureGroup>
                <MapInvalidator />
                <MapInitializer />
              </MapContainer>
            )}
          </div>
        </Form.Item>

        {/* Display address from reverse geocoding */}
        <Form.Item name="adresse_display" label="Adresse détectée">
          <Input readOnly />
        </Form.Item>

        {/* Display coordinates (hidden or for debugging) */}
        {addressData.latitude && addressData.longitude && (
          <div
            style={{ marginBottom: "10px", fontSize: "12px", color: "#888" }}
          >
            Coordonnées du centre: {addressData.latitude.toFixed(6)},{" "}
            {addressData.longitude.toFixed(6)}
          </div>
        )}
      </Form>
    </Drawer>
  );
};
